import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	Divider,
	FormControlLabel,
	Switch,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Alert,
	List,
	ListItem,
	ListItemText,
	Zoom,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCookieConsent } from "../../contexts/CookieConsentContext";
import { getCookiesByCategory } from "../../services/cookieService";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CookieIcon from "@mui/icons-material/Cookie";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import SettingsIcon from "@mui/icons-material/Settings";
import CampaignIcon from "@mui/icons-material/Campaign";

const CookiePreferencesDialog = () => {
	const { t } = useTranslation();
	const {
		showPreferences,
		setShowPreferences,
		consentPreferences,
		savePreferences,
		acceptAll,
		rejectAll,
	} = useCookieConsent();

	const [localPreferences, setLocalPreferences] = useState({
		essential: true,
		analytics: false,
		marketing: false,
	});

	// Initialize local preferences from context when dialog opens
	useEffect(() => {
		if (showPreferences) {
			setLocalPreferences({
				essential: consentPreferences.essential ?? true,
				analytics: consentPreferences.analytics ?? false,
				marketing: consentPreferences.marketing ?? false,
			});
		}
	}, [showPreferences, consentPreferences]);

	const handleToggle = (category) => {
		if (category === "essential") {
			return; // Essential cookies cannot be disabled
		}
		setLocalPreferences((prev) => ({
			...prev,
			[category]: !prev[category],
		}));
	};

	const handleSave = () => {
		savePreferences(localPreferences);
	};

	const handleClose = () => {
		setShowPreferences(false);
	};

	const essentialCookies = getCookiesByCategory("essential");
	const analyticsCookies = getCookiesByCategory("analytics");
	const marketingCookies = getCookiesByCategory("marketing");

	return (
		<Dialog
			open={showPreferences}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			aria-labelledby="cookie-preferences-title"
			PaperProps={{
				sx: {
					borderRadius: "16px",
					maxHeight: "90vh",
				},
			}}
			TransitionComponent={Zoom}
		>
			<DialogTitle
				id="cookie-preferences-title"
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1.5,
					pb: 2,
					color: "text.primary",
					fontWeight: 600,
				}}
			>
				<CookieIcon color="primary" />
				{t("cookie-preferences-title")}
			</DialogTitle>

			<Divider sx={{ mb: 2 }} />

			<DialogContent>
				<Box sx={{ mb: 3 }}>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						{t("cookie-preferences-description")}
					</Typography>

					{/* Essential Cookies */}
					<Accordion defaultExpanded sx={{ mb: 1 }}>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
								<SettingsIcon color="primary" />
								<Box sx={{ flex: 1 }}>
									<FormControlLabel
										control={
											<Switch
												checked={localPreferences.essential}
												disabled
												sx={{ mr: 1 }}
											/>
										}
										label={
											<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
												{t("cookie-category-essential")}
											</Typography>
										}
										sx={{ m: 0 }}
									/>
									<Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
										{t("cookie-category-essential-description")}
									</Typography>
								</Box>
							</Box>
						</AccordionSummary>
						<AccordionDetails>
							<Alert severity="info" sx={{ mb: 2 }}>
								{t("cookie-essential-always-enabled")}
							</Alert>
							{essentialCookies.length > 0 && (
								<List dense>
									{essentialCookies.map((cookie, index) => (
										<ListItem key={index}>
											<ListItemText
												primary={cookie.name}
												secondary={`${t(cookie.purposeKey)} (${t("cookie-expiry")}: ${t(cookie.expiryKey)})`}
											/>
										</ListItem>
									))}
								</List>
							)}
						</AccordionDetails>
					</Accordion>

					{/* Analytics Cookies */}
					<Accordion sx={{ mb: 1 }}>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
								<AnalyticsIcon color="primary" />
								<Box sx={{ flex: 1 }}>
									<FormControlLabel
										control={
											<Switch
												checked={localPreferences.analytics}
												onChange={() => handleToggle("analytics")}
												sx={{ mr: 1 }}
											/>
										}
										label={
											<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
												{t("cookie-category-analytics")}
											</Typography>
										}
										sx={{ m: 0 }}
									/>
									<Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
										{t("cookie-category-analytics-description")}
									</Typography>
								</Box>
							</Box>
						</AccordionSummary>
						<AccordionDetails>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
								{t("cookie-category-analytics-details")}
							</Typography>
							{analyticsCookies.length > 0 && (
								<List dense>
									{analyticsCookies.map((cookie, index) => (
										<ListItem key={index}>
											<ListItemText
												primary={cookie.name}
												secondary={`${t(cookie.purposeKey)} (${t("cookie-expiry")}: ${t(cookie.expiryKey)})`}
											/>
										</ListItem>
									))}
								</List>
							)}
						</AccordionDetails>
					</Accordion>

					{/* Marketing Cookies */}
					<Accordion>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
								<CampaignIcon color="primary" />
								<Box sx={{ flex: 1 }}>
									<FormControlLabel
										control={
											<Switch
												checked={localPreferences.marketing}
												onChange={() => handleToggle("marketing")}
												sx={{ mr: 1 }}
											/>
										}
										label={
											<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
												{t("cookie-category-marketing")}
											</Typography>
										}
										sx={{ m: 0 }}
									/>
									<Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
										{t("cookie-category-marketing-description")}
									</Typography>
								</Box>
							</Box>
						</AccordionSummary>
						<AccordionDetails>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
								{t("cookie-category-marketing-details")}
							</Typography>
							{marketingCookies.length > 0 ? (
								<List dense>
									{marketingCookies.map((cookie, index) => (
										<ListItem key={index}>
											<ListItemText
												primary={cookie.name}
												secondary={`${t(cookie.purposeKey)} (${t("cookie-expiry")}: ${t(cookie.expiryKey)})`}
											/>
										</ListItem>
									))}
								</List>
							) : (
								<Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
									{t("cookie-no-marketing-cookies")}
								</Typography>
							)}
						</AccordionDetails>
					</Accordion>
				</Box>
			</DialogContent>

			<Divider sx={{ mt: 2 }} />

			<DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
				<Button
					onClick={rejectAll}
					variant="outlined"
					color="inherit"
					sx={{
						textTransform: "none",
						fontWeight: "medium",
					}}
				>
					{t("cookie-consent-reject-all")}
				</Button>
				<Button
					onClick={acceptAll}
					variant="outlined"
					sx={{
						textTransform: "none",
						fontWeight: "medium",
						borderColor: "primary.main",
						color: "primary.main",
					}}
				>
					{t("cookie-consent-accept-all")}
				</Button>
				<Button
					onClick={handleClose}
					variant="outlined"
					color="inherit"
					sx={{
						textTransform: "none",
						fontWeight: "medium",
					}}
				>
					{t("cancel")}
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					sx={{
						textTransform: "none",
						fontWeight: "medium",
						backgroundColor: "primary.main",
						"&:hover": {
							backgroundColor: "primary.dark",
						},
					}}
				>
					{t("cookie-preferences-save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CookiePreferencesDialog;

